(function () {
  'use strict';

  const productCache = new Map();
  let processedPrices = new WeakSet();
  let observerInitialized = false;

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString =
      format ||
      (window.theme && window.theme.settings && window.theme.settings.moneyFormat) ||
      '{{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision || 2;
      thousands = thousands || '.';
      decimal = decimal || ',';
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }

    const match = formatString.match(placeholderRegex);
    if (!match) return formatWithDelimiters(cents, 2);

    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  function getPrefix() {
    return (window.theme && window.theme.settings && window.theme.settings.priceFromPrefix) || '';
  }

  function getCardProductData(root) {
    const raw = root.getAttribute('data-product');
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (err) {
      return null;
    }
  }

  function toCents(value) {
    const amount = parseFloat(value);
    if (isNaN(amount)) return null;
    return Math.round(amount * 100);
  }

  function computePricingFromCardData(cardData) {
    if (!cardData) return null;

    const min = toCents(cardData.priceMin);
    const max = toCents(cardData.priceMax ?? cardData.priceMin);
    if (min == null) return null;

    const variants = cardData.variants || [];
    let showFrom = max != null && min !== max;

    if (variants.length > 1) {
      const available = variants.filter((variant) => variant.available !== false);
      const list = available.length ? available : variants;
      const prices = list
        .map((variant) => (typeof variant.price === 'number' ? variant.price : null))
        .filter((price) => price != null);

      if (prices.length > 1) {
        const variantMin = Math.min.apply(null, prices);
        const variantMax = Math.max.apply(null, prices);
        showFrom = variantMin !== variantMax;
        return { min: variantMin, showFrom, variant: null };
      }
    }

    return { min, showFrom, variant: null };
  }

  function extractProductLink(root) {
    const link = root.querySelector('a[href*="/products/"]');
    if (!link) return null;
    try {
      const url = new URL(link.href, window.location.origin);
      const parts = url.pathname.split('/').filter(Boolean);
      const idx = parts.indexOf('products');
      if (idx === -1 || !parts[idx + 1]) return null;
      const handle = decodeURIComponent(parts[idx + 1].split('?')[0]);
      const jsonUrl = url.pathname.replace(/\/$/, '') + '.js';
      return { handle, jsonUrl };
    } catch (err) {
      return null;
    }
  }

  function computePricing(product) {
    const variants = product.variants || [];
    const available = variants.filter((variant) => variant.available);
    const list = available.length ? available : variants;
    if (!list.length) return null;

    const prices = list.map((variant) => variant.price);
    const min = Math.min.apply(null, prices);
    const max = Math.max.apply(null, prices);
    const showFrom = variants.length > 1 && available.length > 0 && min !== max;
    const variant = list.reduce((best, current) => {
      if (!best || current.price < best.price) return current;
      return best;
    }, null);

    return { min, showFrom, variant };
  }

  async function fetchProduct(productLink) {
    if (!productLink || !productLink.jsonUrl) return null;
    if (productCache.has(productLink.jsonUrl)) return productCache.get(productLink.jsonUrl);

    const pending = fetch(productLink.jsonUrl)
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);

    productCache.set(productLink.jsonUrl, pending);
    return pending;
  }

  async function resolvePricing(root) {
    const productLink = extractProductLink(root);
    if (productLink) {
      const product = await fetchProduct(productLink);
      const pricing = product ? computePricing(product) : null;
      if (pricing) return pricing;
    }

    return computePricingFromCardData(getCardProductData(root));
  }

  function findSalePriceEl(root) {
    return root.querySelector(
      '.boost-sd__product-price--sale, .boost-sd__product-price--default, .boost-sd__suggestion-queries-item-price'
    );
  }

  function applyBssAttributes(card, priceEl) {
    const bssEl = card.querySelector('.boost-sd__product-price') || priceEl;
    if (!bssEl) return;
    const productId = card.getAttribute('id') || card.getAttribute('data-product-id');
    if (!productId) return;
    bssEl.setAttribute('bss-b2b-product-id', productId);
    bssEl.setAttribute('bss-b2b-product-price', '');
  }

  function applyFromPrefix(priceEl, prefix) {
    const isSearchPrice = priceEl.classList.contains('boost-sd__suggestion-queries-item-price');
    let host = priceEl;
    let insertBefore = priceEl.firstChild;

    if (isSearchPrice) {
      const amountEl = priceEl.querySelector('.boost-sd__suggestion-queries-item-amount');
      const saleCurrencyEl =
        amountEl?.querySelector('.boost-sd__format-currency--price-sale') ||
        amountEl?.querySelector('.boost-sd__format-currency');

      if (amountEl && saleCurrencyEl) {
        priceEl.querySelector(':scope > .price__from')?.remove();
        host = amountEl;
        insertBefore = saleCurrencyEl;
      }
    }

    let fromEl = host.querySelector(':scope > .price__from');

    if (!prefix) {
      fromEl?.remove();
      return;
    }

    if (!fromEl) {
      fromEl = document.createElement('span');
      fromEl.className = 'price__from';
      host.insertBefore(fromEl, insertBefore);
    }

    fromEl.textContent = prefix.toLowerCase();
  }

  async function patchPriceContainer(root, priceEl) {
    if (!root || !priceEl || processedPrices.has(priceEl)) return;

    const pricing = await resolvePricing(root);
    if (!pricing) return;

    const prefix = getPrefix();
    const formatted = formatMoney(pricing.min);
    const fromPrefix = pricing.showFrom && prefix ? prefix : '';

    priceEl.dataset.cfgPricePatched = 'true';

    applyFromPrefix(priceEl, fromPrefix);

    const currencyEls = priceEl.querySelectorAll('.boost-sd__format-currency');
    if (currencyEls.length) {
      currencyEls[0].textContent = formatted;
    } else if (!fromPrefix) {
      priceEl.textContent = formatted;
    } else {
      let amountEl = priceEl.querySelector(':scope > .boost-sd__format-currency');
      if (!amountEl) {
        amountEl = document.createElement('span');
        amountEl.className = 'boost-sd__format-currency';
        priceEl.appendChild(amountEl);
      }
      amountEl.textContent = formatted;
    }

    processedPrices.add(priceEl);
    applyBssAttributes(root, priceEl);
  }

  function patchProductCard(card) {
    const priceEl = findSalePriceEl(card);
    if (!priceEl || priceEl.dataset.cfgPricePatched === 'true') return;
    patchPriceContainer(card, priceEl);
  }

  function patchSearchItem(item) {
    const priceEl = findSalePriceEl(item);
    if (!priceEl || priceEl.dataset.cfgPricePatched === 'true') return;
    patchPriceContainer(item, priceEl);
  }

  function scan(root) {
    const scope = root || document;
    scope.querySelectorAll('.boost-sd__product-item').forEach(patchProductCard);
    scope.querySelectorAll('.boost-sd__suggestion-queries-item--product').forEach(patchSearchItem);
  }

  function resetPatchState(root) {
    const scope = root || document;
    scope.querySelectorAll('[data-cfg-price-patched]').forEach((el) => {
      delete el.dataset.cfgPricePatched;
    });
    processedPrices = new WeakSet();
  }

  function observeBoost() {
    if (observerInitialized) return;
    observerInitialized = true;

    const targets = document.querySelectorAll(
      '.boost-sd__filter-product-list, .boost-sd__search-result-list, #boost-sd__search-widget-init-wrapper, .boost-sd__product-list'
    );

    if (!targets.length) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          if (node.matches && node.matches('.boost-sd__product-item')) {
            patchProductCard(node);
          } else if (node.matches && node.matches('.boost-sd__suggestion-queries-item--product')) {
            patchSearchItem(node);
          }
          scan(node);
        });
      });
    });

    targets.forEach((target) => observer.observe(target, { childList: true, subtree: true }));
  }

  function runWithRetries(reset) {
    if (reset) resetPatchState(document);
    scan(document);

    let attempts = 0;
    const interval = setInterval(() => {
      scan(document);
      attempts += 1;
      if (attempts > 20) clearInterval(interval);
    }, 300);
  }

  function init() {
    observeBoost();
    runWithRetries(false);
  }

  function refresh() {
    runWithRetries(true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('on:bfcache:load-restore', refresh);
})();
